import { ApolloError, ApolloQueryResult } from '@apollo/client';
import { useMediaQuery } from '@chakra-ui/react';
import {
  useGetListingReportByShareLinkTokenQuery,
  useGetListingReportQuery,
  useGetSingleUserCapturedPropertyDetailsQuery,
  useGetUserCapturedPropertyDetailsByTokenQuery,
  usePublicListingQuery,
} from '@client/graphql/__generated__/main-operations';
import {
  ChartCardFragment,
  Exact,
  ListingComparableSetForAgentQuery,
  ListingComparableSetFragment,
  ListingComparableSetInput,
  PropertyDetailsFragment,
  PublicListingFragment,
  ReportConfidenceFragment,
  TableDataFragment,
  UpdateListingComparableInput,
} from '@client/graphql/__generated__/types';
import { createContext, PropsWithChildren, useContext, useState } from 'react';
import { useParams } from 'react-router';
import { ComparableState } from '~/apps/consumer/pages/ListingSetup/Comparables/ComparablesPage';
import { MUTE_ERROR_TOAST_QUERY_OPTIONS } from '~/services/document/utils/apollo-client';
import { useAppContext } from '~/services/main/contexts/AppContext';
import { useListingReportComps } from './hooks/useListingReportComps';

export enum ListingReportChartType {
  LINE = 'LINE',
  BAR = 'BAR',
}

interface ListingPageContextType {
  listing?: PublicListingFragment;
  propertyDetails?: PropertyDetailsFragment;
  metrics?: ChartCardFragment[];
  confidence?: ReportConfidenceFragment;
  tables?: TableDataFragment[];
  isLoadingListing: boolean;
  isLoadingReport: boolean;
  includeIndigoData: boolean;
  setIncludeIndigoData: (includeIndigoData: boolean) => void;
  reportError?: ApolloError;
  isViewOnly?: boolean;
  isOffMarketListing?: boolean;
  isMyListing?: boolean;
  comps: ComparableState[];
  setComps: (comps: ComparableState[]) => void;
  isDirty: boolean;
  setIsDirty: (isDirty: boolean) => void;
  updateComps: (comps: UpdateListingComparableInput[]) => Promise<void>;
  comparableSet?: ListingComparableSetFragment;
  comparableSetLoading: boolean;
  refetchComparableSet: (
    variables?: Partial<Exact<{ input: ListingComparableSetInput }>> | undefined
  ) => Promise<ApolloQueryResult<ListingComparableSetForAgentQuery>>;
  isPrint: boolean;
}

export const ListingPageContext = createContext<ListingPageContextType>(
  {} as ListingPageContextType
);

interface ListingPageProviderProps extends PropsWithChildren {
  isViewOnly?: boolean;
}

export const ListingPageProvider = ({
  isViewOnly,
  children,
}: ListingPageProviderProps) => {
  const [isPrint] = useMediaQuery('print');
  const { propertySlug, token, propertyDetailsId } = useParams();
  // ! TODO: dynamically set this based on premium user
  const [includeIndigoData, setIncludeIndigoData] = useState(true);
  const { user } = useAppContext();

  const { data, loading: isLoadingListing } = usePublicListingQuery({
    variables: {
      input: {
        propertySlug: propertySlug || '',
      },
    },
    skip: !propertySlug,
  });

  const { data: propertyDetailsData, loading: isLoadingPropertyDetails } =
    useGetSingleUserCapturedPropertyDetailsQuery({
      variables: {
        propertyDetailsId: propertyDetailsId || '',
      },
      skip: !propertyDetailsId || !!token,
    });

  const {
    data: propertyDetailsByTokenData,
    loading: isLoadingPropertyDetailsByToken,
  } = useGetUserCapturedPropertyDetailsByTokenQuery({
    variables: {
      input: {
        propertyDetailsId: propertyDetailsId || '',
        token: token || '',
      },
    },
    skip: !token || !propertyDetailsId,
  });

  const propertyDetails =
    propertyDetailsData?.getSingleUserCapturedPropertyDetails ||
    propertyDetailsByTokenData?.getUserCapturedPropertyDetailsByToken;

  const {
    data: listingReportData,
    loading: isLoadingReport,
    error: listingReportError,
  } = useGetListingReportQuery({
    variables: {
      input: {
        listingId: data?.publicListing?.id,
        propertyDetailsId:
          propertyDetailsData?.getSingleUserCapturedPropertyDetails?.id,
        useIndigoData: includeIndigoData,
      },
    },
    skip:
      (!data?.publicListing?.id &&
        !propertyDetailsData?.getSingleUserCapturedPropertyDetails?.id) ||
      !!token,
    // NOTE: we don't want to show an error toast because we now have a custom error state
    ...MUTE_ERROR_TOAST_QUERY_OPTIONS,
  });

  const {
    data: sharedListingReportData,
    loading: isLoadingSharedListingReport,
    error: sharedListingReportError,
  } = useGetListingReportByShareLinkTokenQuery({
    variables: {
      input: {
        token: token || '',
        listingId: data?.publicListing?.id || '',
        propertyDetailsId: propertyDetails?.id || '',
        useIndigoData: includeIndigoData,
      },
    },
    skip: !token || (!data?.publicListing?.id && !propertyDetails?.id),
  });

  const charts = !!token
    ? sharedListingReportData?.getListingReportByShareLinkToken?.charts
    : listingReportData?.getListingReport?.charts;
  const tables = !!token
    ? sharedListingReportData?.getListingReportByShareLinkToken?.tables
    : listingReportData?.getListingReport?.tables;

  const confidence = !!token
    ? sharedListingReportData?.getListingReportByShareLinkToken?.confidence
    : listingReportData?.getListingReport?.confidence;

  const reportError = token ? sharedListingReportError : listingReportError;

  const listing = data?.publicListing;

  const {
    comps,
    setComps,
    isDirty,
    setIsDirty,
    updateComps,
    comparableSet,
    comparableSetLoading,
    refetchComparableSet,
  } = useListingReportComps({
    listing,
    propertyDetails,
    isViewOnly,
    token,
  });

  return (
    <ListingPageContext.Provider
      value={{
        listing,
        propertyDetails,
        metrics: charts,
        tables,
        confidence,
        isLoadingListing:
          isLoadingListing ||
          isLoadingPropertyDetails ||
          isLoadingPropertyDetailsByToken,
        isLoadingReport: isLoadingSharedListingReport || isLoadingReport,
        includeIndigoData,
        setIncludeIndigoData,
        reportError,
        isViewOnly,
        isOffMarketListing: !!propertyDetailsId,
        isMyListing: user?.id === data?.publicListing?.listingAgent?.user?.id,
        comps,
        setComps,
        isDirty,
        setIsDirty,
        updateComps,
        comparableSet,
        comparableSetLoading,
        refetchComparableSet,
        isPrint,
      }}
    >
      {children}
    </ListingPageContext.Provider>
  );
};

export const useListingPageContext = () => {
  return useContext(ListingPageContext);
};
